/**
 * AK-938 — «Закрыть опыт» (ADR-086 D9, low-fi 5·А).
 *
 * Урожайность варианта бэкенд берёт из посева зоны, а если его нет — из уборки
 * по зоне (операция «Уборка» с `subzone_id` и `attrs.harvested_yield_t`, т с
 * варианта). Поэтому «Закрыть с урожайностью» = по одной уборке на каждый
 * вариант без урожайности + `POST …/close`. Тонны считаем из ц/га × площадь.
 *
 * Ввод ц/га — по правилу «пусто ≠ мусор»: пусто у варианта без урожайности —
 * закрыть с урожайностью нельзя (словами), мусор — ошибка с примером.
 */
import { parseRate } from './schemeDraft';
import type { TrialVariantSummary } from './types';

export interface HarvestOp {
  subzone_id: string;
  label: string;
  area_ha: number;
  harvested_t: number;
}

export type HarvestPlan =
  | { ok: true; ops: HarvestOp[] }
  | { ok: false; errors: Record<string, string> };

/** ц/га по вариантам → уборки для тех, у кого урожайности ещё нет. */
export function planHarvest(
  variants: TrialVariantSummary[],
  typed: Record<string, string>,
): HarvestPlan {
  const errors: Record<string, string> = {};
  const ops: HarvestOp[] = [];
  for (const v of variants) {
    if (v.yield_c_ha != null) continue;
    const parsed = parseRate(typed[v.subzone_id] ?? '');
    if (!parsed.ok) {
      errors[v.subzone_id] = 'Нужно число в ц/га, например 52,4';
      continue;
    }
    if (parsed.value == null) {
      errors[v.subzone_id] = 'Укажите урожайность варианта, ц/га';
      continue;
    }
    if (!v.area_ha) {
      errors[v.subzone_id] = 'У контура варианта нет площади — поправьте контур';
      continue;
    }
    const tonnes = Math.round(((parsed.value / 10) * v.area_ha) * 1000) / 1000;
    ops.push({ subzone_id: v.subzone_id, label: v.label, area_ha: v.area_ha, harvested_t: tonnes });
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, ops };
}

/** Тип операции «Уборка» компании: по коду справочника, иначе по названию. */
export function findHarvestType<T extends { id: string; local_name: string; ref_code?: string | null }>(
  types: T[],
): T | null {
  return (
    types.find((t) => /harvest/i.test(t.ref_code ?? '')) ??
    types.find((t) => /уборк/i.test(t.local_name)) ??
    null
  );
}

export const INCOMPLETE_REASONS = [
  'Градобой',
  'Засуха',
  'Вымерзание',
  'Полегание',
  'Уборку не провели',
  'Другое',
] as const;

/** Причина «недоделанного» — обязательна: «Градобой» или «Градобой: край у дороги». */
export function incompleteNote(reason: string, comment: string): string | null {
  const r = reason.trim();
  const c = comment.trim();
  if (!r && !c) return null;
  if (!r) return c;
  return c ? `${r}: ${c}` : r;
}
